import React, { useState, useEffect } from 'react';
import Header from '../../components/admin/Header';
import Sidebar from '../../components/admin/Sidebar';
import Footer from '../../components/admin/Footer';
import { 
  User, 
  Mail, 
  Phone, 
  Calendar,
  Tablet,
  UsersIcon,
  X,
  Clock,
  CheckCircle,
  XCircle,
  Eye,
  Search,
  Filter,
  Download,
  RefreshCw,
  LogIn,
  LogOut,
  Activity,
  Shield,
  Smartphone,
  Monitor,
  Globe,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  FileText
} from 'lucide-react';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const LoginHistory = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [loginHistory, setLoginHistory] = useState([]);
  const [filteredHistory, setFilteredHistory] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterMethod, setFilterMethod] = useState('all');
  const [filterSuccess, setFilterSuccess] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [stats, setStats] = useState({
    totalLogins: 0,
    successfulLogins: 0,
    failedLogins: 0,
    activeSessions: 0,
    uniqueUsers: 0
  });

  // API Configuration
  // const apiUrl = process.env.REACT_APP_API_URL;
  const apiUrl = process.env.REACT_APP_API_URL || 'https://api.marketmaestroovip.in';
  const axiosConfig = {
    baseURL: apiUrl,
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
    }
  };

  // Fetch users on component mount
  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/auth/users', axiosConfig);
      
      if (response.data.success) {
        setUsers(response.data.data);
        // Fetch login history for stats
        fetchLoginHistory();
      } else {
        toast.error('Failed to fetch users');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error(error.response?.data?.error || 'Failed to fetch users');
      setLoading(false);
    }
  };

  const fetchLoginHistory = async (userId = null) => {
    try {
      let url = '/api/auth/login-history';
      if (userId) {
        url += `?userId=${userId}`;
      }
      
      const response = await axios.get(url, axiosConfig);
      
      if (response.data.success) {
        const historyData = response.data.data;
        setLoginHistory(historyData);
        
        // Calculate stats
        if (!userId) {
          calculateStats(historyData);
        }
        
        // Filter initial data
        applyFilters(historyData, searchTerm, filterMethod, filterSuccess);
      }
    } catch (error) {
        console.error('Error fetching login history:', error);
        // Provide more detailed error message
        const errorMessage = error.response?.data?.error 
            || error.response?.data?.message 
            || error.message 
            || 'Failed to fetch login history';
        toast.error(errorMessage);
    } finally {
      if (!userId) {
        setLoading(false);
      }
    }
  };

  const fetchUserLoginHistory = async (user) => {
    try {
      setLoadingHistory(true);
      setSelectedUser(user);
      
      const response = await axios.get(`/api/auth/login-history?userId=${user.id}`, axiosConfig);
      
      if (response.data.success) {
        setFilteredHistory(response.data.data);
        setHistoryModalOpen(true);
      } else {
        toast.error('Failed to fetch login history');
      }
    }  catch (error) {
  console.error('Error fetching login history:', error);
  // Provide more detailed error message
  const errorMessage = error.response?.data?.error 
    || error.response?.data?.message 
    || error.message 
    || 'Failed to fetch login history';
  toast.error(errorMessage);
} finally {
      setLoadingHistory(false);
    }
  };

  const calculateStats = (history) => {
    const totalLogins = history.length;
    const successfulLogins = history.filter(log => log.success === 1).length;
    const failedLogins = history.filter(log => log.success === 0).length;
    
    // Get unique users from login history
    const uniqueUserIds = [...new Set(history.map(log => log.user_id).filter(id => id))];
    const uniqueUsers = uniqueUserIds.length;
    
    // Get active sessions (you might need to implement this based on your session management)
    const activeSessions = users.filter(user => {
      // This is a placeholder - implement based on your session logic
      return user.last_login && new Date(user.last_login) > new Date(Date.now() - 24 * 60 * 60 * 1000);
    }).length;
    
    setStats({
      totalLogins,
      successfulLogins,
      failedLogins,
      activeSessions,
      uniqueUsers
    });
  };

  const applyFilters = (data, search, method, success) => {
    let filtered = [...data];
    
    // Apply search filter
    if (search) {
      const term = search.toLowerCase();
      filtered = filtered.filter(log => 
        (log.email && log.email.toLowerCase().includes(term)) ||
        (log.ip_address && log.ip_address.includes(term)) ||
        (log.method && log.method.toLowerCase().includes(term))
      );
    }
    
    // Apply method filter
    if (method !== 'all') {
      filtered = filtered.filter(log => log.method === method);
    }
    
    // Apply success filter
    if (success !== 'all') {
      const successValue = success === 'success' ? 1 : 0;
      filtered = filtered.filter(log => log.success === successValue);
    }
    
    setFilteredHistory(filtered);
    setCurrentPage(1);
  };

  useEffect(() => {
    applyFilters(loginHistory, searchTerm, filterMethod, filterSuccess);
  }, [searchTerm, filterMethod, filterSuccess, loginHistory]);

  // Handle body scroll when sidebar is open on mobile
  useEffect(() => {
    if (sidebarOpen && window.innerWidth < 1024) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [sidebarOpen]);

  // Format date and time
  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Get status badge
  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800 border border-green-200';
      case 'inactive':
        return 'bg-yellow-100 text-yellow-800 border border-yellow-200';
      case 'deleted':
        return 'bg-red-100 text-red-800 border border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border border-gray-200';
    }
  };

  // Get login method badge
  const getMethodBadge = (method) => {
    switch (method) {
      case 'password':
        return 'bg-blue-100 text-blue-800 border border-blue-200';
      case 'otp':
        return 'bg-purple-100 text-purple-800 border border-purple-200';
      default:
        return 'bg-gray-100 text-gray-800 border border-gray-200';
    }
  };

  // Get success/failure badge
  const getSuccessBadge = (success) => {
    return success === 1 
      ? 'bg-green-100 text-green-800 border border-green-200 flex items-center gap-1'
      : 'bg-red-100 text-red-800 border border-red-200 flex items-center gap-1';
  };

  // Get device icon
  const getDeviceIcon = (userAgent) => {
    if (!userAgent) return <Monitor className="h-4 w-4" />;
    
    const ua = userAgent.toLowerCase();
    if (ua.includes('mobile') || ua.includes('android') || ua.includes('iphone')) {
      return <Smartphone className="h-4 w-4" />;
    } else if (ua.includes('tablet') || ua.includes('ipad')) {
      return <Tablet className="h-4 w-4" />;
    } else {
      return <Monitor className="h-4 w-4" />;
    }
  };

  // Get location info (simplified - in real app you'd use IP geolocation API)
  const getLocationInfo = (ip) => {
    if (!ip || ip === '::1' || ip === '127.0.0.1') return 'Localhost';
    
    // Simple IP classification (you should use a proper geolocation service)
    if (ip.startsWith('192.168.') || ip.startsWith('10.')) return 'Private Network';
    
    return ip;
  };

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredHistory.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredHistory.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Export to CSV
  const exportToCSV = () => {
    const headers = ['ID', 'User ID', 'Email', 'Method', 'Success', 'IP Address', 'User Agent', 'Created At'];
    const csvContent = [
      headers.join(','),
      ...filteredHistory.map(log => [
        log.id,
        log.user_id || '',
        log.email || '',
        log.method || '',
        log.success ? 'Yes' : 'No',
        log.ip_address || '',
        `"${(log.user_agent || '').replace(/"/g, '""')}"`,
        log.created_at || ''
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `login-history-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    
    toast.success('Login history exported successfully');
  };

  // Refresh data
  const refreshData = () => {
    setLoading(true);
    fetchUsers();
    toast.info('Refreshing login history...');
  };

  // Close history modal
  const closeHistoryModal = () => {
    setHistoryModalOpen(false);
    setSelectedUser(null);
    setFilteredHistory([]);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100">
      <ToastContainer position="bottom-right"autoClose={3000} />
      
      <div className="flex h-screen">
        {/* Sidebar */}
        <Sidebar isOpen={sidebarOpen} />
        
        {/* Main Content Area */}
        <div className="flex-1 flex flex-col min-h-screen overflow-hidden lg:ml-64">
          {/* Header */}
          <Header toggleSidebar={() => setSidebarOpen(!sidebarOpen)} />

          {/* Main Content */}
          <main className="flex-1 overflow-y-auto p-4 md:p-6">
            {/* Page Header */}
            <div className="mb-6 md:mb-8">
              <div className="flex flex-col md:flex-row md:items-center justify-between">
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
                    Login History
                  </h1>
                  <p className="text-sm md:text-base text-gray-600 dark:text-gray-400 mt-1 md:mt-2">
                    Monitor and track all user login activities
                  </p>
                </div>
                
                <div className="flex gap-3 mt-4 md:mt-0">
                  <button
                    onClick={exportToCSV}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <Download className="h-4 w-4" />
                    Export CSV
                  </button>
                  <button
                    onClick={refreshData}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <RefreshCw className="h-4 w-4" />
                    Refresh
                  </button>
                </div>
              </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Total Logins</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalLogins}</p>
                  </div>
                  <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                    <LogIn className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Successful</p>
                    <p className="text-2xl font-bold text-green-600">{stats.successfulLogins}</p>
                  </div>
                  <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                    <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Failed</p>
                    <p className="text-2xl font-bold text-red-600">{stats.failedLogins}</p>
                  </div>
                  <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center">
                    <XCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700 hidden">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Active Sessions</p>
                    <p className="text-2xl font-bold text-purple-600">{stats.activeSessions}</p>
                  </div>
                  <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                    <Activity className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Unique Users</p>
                    <p className="text-2xl font-bold text-amber-600">{stats.uniqueUsers}</p>
                  </div>
                  <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/30 rounded-lg flex items-center justify-center">
                    <UsersIcon className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                  </div>
                </div>
              </div>
            </div>

            {/* Filters */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 md:p-6 mb-6">
              <div className="flex flex-col md:flex-row gap-4">
                {/* Search Input */}
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search by email, IP address, or method..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                
                {/* Method Filter */}
                <div className="w-full md:w-48">
                  <div className="relative">
                    <select
                      value={filterMethod}
                      onChange={(e) => setFilterMethod(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none"
                    >
                      <option value="all">All Methods</option>
                      <option value="password">Password</option>
                      <option value="otp">OTP</option>
                    </select>
                    <Filter className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
                  </div>
                </div>

                {/* Success Filter */}
                <div className="w-full md:w-48">
                  <div className="relative">
                    <select
                      value={filterSuccess}
                      onChange={(e) => setFilterSuccess(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none"
                    >
                      <option value="all">All Status</option>
                      <option value="success">Successful</option>
                      <option value="failed">Failed</option>
                    </select>
                    <Filter className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
                  </div>
                </div>
              </div>
            </div>

            {/* Login History Table */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden mb-6">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Recent Login Activity ({filteredHistory.length})
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Latest login attempts across all users
                    </p>
                  </div>
                  <div className="text-sm text-gray-500">
                    Showing {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, filteredHistory.length)} of {filteredHistory.length}
                  </div>
                </div>
              </div>

              {filteredHistory.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64 text-center p-8">
                  <FileText className="h-16 w-16 text-gray-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                    No login history found
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    {searchTerm || filterMethod !== 'all' || filterSuccess !== 'all'
                      ? 'Try adjusting your search or filters'
                      : 'No login activity recorded yet'}
                  </p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            User
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Method
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            IP Address
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Device
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Time
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {currentItems.map((log) => (
                          <tr key={log.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div>
                                <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                  {log.email || 'Unknown'}
                                </div>
                                <div className="text-sm text-gray-500 dark:text-gray-400">
                                  User ID: {log.user_id || 'N/A'}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getMethodBadge(log.method)}`}>
                                {log.method === 'otp' ? 'OTP' : 'Password'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getSuccessBadge(log.success)}`}>
                                {log.success === 1 ? (
                                  <>
                                    <CheckCircle className="h-3 w-3" />
                                    Success
                                  </>
                                ) : (
                                  <>
                                    <XCircle className="h-3 w-3" />
                                    Failed
                                  </>
                                )}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center text-sm text-gray-900 dark:text-gray-100">
                                <Globe className="h-4 w-4 mr-2 text-gray-400" />
                                {getLocationInfo(log.ip_address)}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                                {getDeviceIcon(log.user_agent)}
                                <span className="ml-2 truncate max-w-xs">
                                  {log.user_agent ? 
                                    (log.user_agent.includes('Mobile') ? 'Mobile' : 
                                     log.user_agent.includes('Tablet') ? 'Tablet' : 'Desktop') 
                                    : 'Unknown'}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {formatDateTime(log.created_at)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                      <div className="flex items-center justify-between">
                        <div className="text-sm text-gray-700 dark:text-gray-300">
                          Page {currentPage} of {totalPages}
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => paginate(currentPage - 1)}
                            disabled={currentPage === 1}
                            className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <ChevronLeft className="h-4 w-4" />
                          </button>
                          {[...Array(totalPages)].map((_, i) => (
                            <button
                              key={i + 1}
                              onClick={() => paginate(i + 1)}
                              className={`px-3 py-1 rounded-lg ${
                                currentPage === i + 1
                                  ? 'bg-blue-600 text-white'
                                  : 'border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                              }`}
                            >
                              {i + 1}
                            </button>
                          ))}
                          <button
                            onClick={() => paginate(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <ChevronRight className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Users List */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden mb-6">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Registered Users ({users.length})
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Click on any user to view their login history
                </p>
              </div>

              {loading ? (
                <div className="flex justify-center items-center h-64">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
              ) : users.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64 text-center p-8">
                  <UsersIcon className="h-16 w-16 text-gray-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                    No users found
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    No users have been registered yet
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          User
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Contact
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider hidden">
                          Member
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Last Login
                        </th>
                        {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Actions
                        </th> */}
                      </tr>
                    </thead>
                    
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {users.map((user) => (
                        
                        <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer" onClick={() => fetchUserLoginHistory(user)}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-10 w-10 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                                <User className="h-6 w-6 text-white" />
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                  {user.username || 'N/A'}
                                </div>
                                <div className="text-sm text-gray-500 dark:text-gray-400">
                                  ID: {user.id}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="space-y-1">
                              <div className="flex items-center text-sm text-gray-900 dark:text-gray-100">
                                <Mail className="h-4 w-4 mr-2 text-gray-400" />
                                {user.email || 'N/A'}
                              </div>
                              <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                                <Phone className="h-4 w-4 mr-2 text-gray-400" />
                                {user.phone || 'N/A'}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getStatusBadge(user.status)}`}>
                              {user.status === 'active' && <CheckCircle className="h-3 w-3 mr-1" />}
                              {user.status === 'inactive' && <Clock className="h-3 w-3 mr-1" />}
                              {user.status === 'deleted' && <XCircle className="h-3 w-3 mr-1" />}
                              {user.status?.charAt(0).toUpperCase() + user.status?.slice(1) || 'Unknown'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap hidden">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                              user.is_member === 1 
                                ? 'bg-blue-100 text-blue-800 border border-blue-200' 
                                : 'bg-gray-100 text-gray-800 border border-gray-200'
                            }`}>
                              {user.is_member === 1 ? 'Premium' : 'Basic'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            {user.last_login ? formatDateTime(user.last_login) : 'Never'}
                          </td>
                          {/* <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <button
                              onClick={() => fetchUserLoginHistory(user)}
                              className="inline-flex items-center px-3 py-1.5 text-sm bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-md hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors"
                              disabled={loadingHistory}
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              View History
                            </button>
                          </td> */}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </main>
          {/* Footer */}
          <Footer />
        </div>
      </div>

      {/* User Login History Modal */}
      {historyModalOpen && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                    Login History: {selectedUser.username}
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {selectedUser.email} • ID: {selectedUser.id}
                  </p>
                </div>
                <button
                  onClick={closeHistoryModal}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              {/* User Info Summary */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Calendar className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Joined</p>
                      <p className="font-medium">{formatDate(selectedUser.created_at)}</p>
                    </div>
                  </div>
                </div>
                <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                  <div className="flex items-center gap-3">
                    <LogIn className="h-5 w-5 text-green-600 dark:text-green-400" />
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Last Login</p>
                      <p className="font-medium">{selectedUser.last_login ? formatDateTime(selectedUser.last_login) : 'Never'}</p>
                    </div>
                  </div>
                </div>
                <div className={`p-4 rounded-lg ${
                  selectedUser.status === 'active' 
                    ? 'bg-green-50 dark:bg-green-900/20' 
                    : selectedUser.status === 'inactive'
                    ? 'bg-yellow-50 dark:bg-yellow-900/20'
                    : 'bg-red-50 dark:bg-red-900/20'
                }`}>
                  <div className="flex items-center gap-3">
                    <Activity className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Current Status</p>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        selectedUser.status === 'active' 
                          ? 'bg-green-100 text-green-800 border border-green-200' 
                          : selectedUser.status === 'inactive'
                          ? 'bg-yellow-100 text-yellow-800 border border-yellow-200'
                          : 'bg-red-100 text-red-800 border border-red-200'
                      }`}>
                        {selectedUser.status?.charAt(0).toUpperCase() + selectedUser.status?.slice(1) || 'Unknown'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Login History Table */}
              {loadingHistory ? (
                <div className="flex justify-center items-center h-64">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
              ) : filteredHistory.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                    No login history found
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    This user hasn't attempted any logins yet
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Date & Time
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Method
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          IP Address
                        </th>
                        {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Device
                        </th> */}
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {filteredHistory.map((log) => (
                        <tr key={log.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                            {formatDateTime(log.created_at)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getMethodBadge(log.method)}`}>
                              {log.method === 'otp' ? 'OTP' : 'Password'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getSuccessBadge(log.success)}`}>
                              {log.success === 1 ? (
                                <>
                                  <CheckCircle className="h-3 w-3" />
                                  Success
                                </>
                              ) : (
                                <>
                                  <XCircle className="h-3 w-3" />
                                  Failed
                                </>
                              )}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                            <div className="flex items-center">
                              <Globe className="h-4 w-4 mr-2 text-gray-400" />
                              {getLocationInfo(log.ip_address)}
                            </div>
                          </td>
                          {/* <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                              {getDeviceIcon(log.user_agent)}
                              <span className="ml-2 truncate max-w-xs">
                                {log.user_agent ? 
                                  (log.user_agent.includes('Mobile') ? 'Mobile' : 
                                   log.user_agent.includes('Tablet') ? 'Tablet' : 'Desktop') 
                                  : ''}
                              </span>
                            </div>
                          </td> */}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Summary Stats */}
              {filteredHistory.length > 0 && (
                <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    Summary
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                      <p className="text-sm text-gray-600 dark:text-gray-400">Total Attempts</p>
                      <p className="text-2xl font-bold">{filteredHistory.length}</p>
                    </div>
                    <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg">
                      <p className="text-sm text-gray-600 dark:text-gray-400">Successful</p>
                      <p className="text-2xl font-bold text-green-600">
                        {filteredHistory.filter(log => log.success === 1).length}
                      </p>
                    </div>
                    <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">
                      <p className="text-sm text-gray-600 dark:text-gray-400">Failed</p>
                      <p className="text-2xl font-bold text-red-600">
                        {filteredHistory.filter(log => log.success === 0).length}
                      </p>
                    </div>
                    <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
                      <p className="text-sm text-gray-600 dark:text-gray-400">Last Attempt</p>
                      <p className="font-medium">
                        {filteredHistory.length > 0 
                          ? formatDateTime(filteredHistory[0].created_at) 
                          : 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-end mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={closeHistoryModal}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginHistory;